import React, { useEffect, useState } from 'react';
import axios from '../../utils/axiosConfig';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Card, 
  CardContent, 
  CardActions, 
  Grid, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  MenuItem, 
  Tooltip, 
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Snackbar,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  AdminPanelSettings as AdminIcon,
  Visibility as ViewIcon,
  EditNote as EditNoteIcon,
  PersonAdd as PersonAddIcon,
  Info as InfoIcon
} from '@mui/icons-material';

const CompanyMembers = () => {
  const [companies, setCompanies] = useState([]);
  const [members, setMembers] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [form, setForm] = useState({ 
    user_id: '', 
    role: 'member',
    permissions: []
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const roleOptions = [
    { value: 'owner', label: 'Eigentümer', color: 'error', icon: <AdminIcon /> },
    { value: 'admin', label: 'Administrator', color: 'warning', icon: <AdminIcon /> },
    { value: 'editor', label: 'Editor', color: 'primary', icon: <EditNoteIcon /> },
    { value: 'viewer', label: 'Betrachter', color: 'info', icon: <ViewIcon /> },
    { value: 'member', label: 'Mitglied', color: 'default', icon: <PersonIcon /> }
  ];

  const loadCompanies = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/companies/`);
      setCompanies(res.data);
      if (res.data.length > 0) {
        setSelectedCompany(res.data[0].id);
        loadCompanyMembers(res.data[0].id);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Firmen:', error);
      setSnackbar({ open: true, message: 'Fehler beim Laden der Firmen', severity: 'error' });
    }
  };

  const loadUsers = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/users`);
      setUsers(res.data);
    } catch (error) {
      console.error('Fehler beim Laden der Benutzer:', error);
      setSnackbar({ open: true, message: 'Fehler beim Laden der Benutzer', severity: 'error' });
    }
  };

  const loadCompanyMembers = async (companyId) => {
    if (!companyId) {
      setMembers([]);
      return;
    }
    
    try {
      setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/company-members/${companyId}`);
      setMembers(res.data);
    } catch (error) {
      console.error('Fehler beim Laden der Mitglieder:', error);
      setSnackbar({ open: true, message: 'Fehler beim Laden der Mitglieder', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompanies();
    loadUsers();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      loadCompanyMembers(selectedCompany);
    }
  }, [selectedCompany]);

  const filteredMembers = members.filter(member =>
    member.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCompanyChange = (companyId) => {
    setSelectedCompany(companyId);
    setSearchTerm('');
  };

  const handleAddMember = () => {
    setEditingMember(null);
    setForm({ user_id: '', role: 'member', permissions: [] });
    setOpen(true);
  };

  const handleEditMember = (member) => {
    setEditingMember(member);
    setForm({
      user_id: member.user_id,
      role: member.role,
      permissions: member.permissions || []
    });
    setOpen(true);
  };

  const handleSaveMember = async () => {
    if (!selectedCompany) return;
    
    try {
      setSaving(true);
      
      if (editingMember) {
        // Update existing member
        await axios.put(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/company-members/${selectedCompany}/${editingMember.id}`, form);
        setSnackbar({ open: true, message: 'Mitglied erfolgreich aktualisiert', severity: 'success' });
      } else {
        // Add new member
        await axios.post(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/company-members/${selectedCompany}`, form);
        setSnackbar({ open: true, message: 'Mitglied erfolgreich hinzugefügt', severity: 'success' });
      }
      
      setOpen(false);
      setEditingMember(null);
      setForm({ user_id: '', role: 'member', permissions: [] });
      await loadCompanyMembers(selectedCompany);
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      setSnackbar({ open: true, message: 'Fehler beim Speichern', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!selectedCompany) return;
    
    if (window.confirm('Sind Sie sicher, dass Sie dieses Mitglied entfernen möchten?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL || 'https://backend.mtsdemo.de'}/api/company-members/${selectedCompany}/${memberId}`);
        await loadCompanyMembers(selectedCompany);
        setSnackbar({ open: true, message: 'Mitglied erfolgreich entfernt', severity: 'success' });
      } catch (error) {
        console.error('Fehler beim Löschen:', error);
        setSnackbar({ open: true, message: 'Fehler beim Löschen', severity: 'error' });
      }
    }
  };

  const getRoleInfo = (role) => {
    return roleOptions.find(r => r.value === role) || roleOptions[4];
  };

  const selectedCompanyData = companies.find(c => c.id === selectedCompany);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PersonAddIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4">Mitglieder</Typography>
        </Box>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={handleAddMember}
          disabled={!selectedCompany}
          sx={{ borderRadius: 2 }}
        >
          Mitglied hinzufügen
        </Button>
      </Box>

      {/* Firmenauswahl */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <BusinessIcon color="primary" />
            <Typography variant="h6">Firma auswählen</Typography>
          </Box>
          <FormControl fullWidth>
            <InputLabel>Firma</InputLabel>
            <Select
              value={selectedCompany}
              onChange={(e) => handleCompanyChange(e.target.value)}
              label="Firma"
            >
              {companies.map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon />
                    <Box>
                      <Typography variant="body1">{company.display_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {company.slug}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {selectedCompanyData && (
        <>
          {/* Suchfeld */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <TextField
                fullWidth
                placeholder="Mitglieder durchsuchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {filteredMembers.length} von {members.length} Mitgliedern angezeigt
              </Typography>
            </CardContent>
          </Card>

          {/* Mitglieder-Liste */}
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  <TableCell><strong>Mitglied</strong></TableCell>
                  <TableCell><strong>E-Mail</strong></TableCell>
                  <TableCell><strong>Rolle</strong></TableCell>
                  <TableCell><strong>Hinzugefügt</strong></TableCell>
                  <TableCell align="center"><strong>Aktionen</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMembers.map((member) => {
                  const roleInfo = getRoleInfo(member.role);
                  return (
                    <TableRow key={member.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            {member.user?.name?.charAt(0) || '?'}
                          </Avatar>
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              {member.user?.name || 'Unbekannter Benutzer'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {member.user_id}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {member.user?.email || 'Nicht verfügbar'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={roleInfo.icon}
                          label={roleInfo.label}
                          color={roleInfo.color}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {member.created_at ? new Date(member.created_at).toLocaleDateString('de-DE') : 'Unbekannt'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="Bearbeiten">
                            <IconButton 
                              size="small" 
                              onClick={() => handleEditMember(member)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Entfernen">
                            <IconButton 
                              size="small" 
                              onClick={() => handleDeleteMember(member.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredMembers.length === 0 && !loading && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <PersonIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {searchTerm ? 'Keine Mitglieder gefunden' : 'Noch keine Mitglieder vorhanden'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchTerm ? 'Versuchen Sie einen anderen Suchbegriff' : 'Fügen Sie das erste Mitglied hinzu'}
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Dialog für Mitglied hinzufügen/bearbeiten */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {editingMember ? 'Mitglied bearbeiten' : 'Neues Mitglied hinzufügen'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Benutzer auswählen</InputLabel>
              <Select
                value={form.user_id}
                onChange={(e) => setForm({ ...form, user_id: e.target.value })}
                label="Benutzer auswählen"
                disabled={editingMember !== null}
              >
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}>
                        {user.name?.charAt(0) || '?'}
                      </Avatar>
                      <Box>
                        <Typography variant="body1">{user.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Rolle</InputLabel>
              <Select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                label="Rolle"
              >
                {roleOptions.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {role.icon}
                      <Typography>{role.label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {form.role && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>{getRoleInfo(form.role).label}:</strong> {
                    form.role === 'owner' ? 'Vollzugriff auf alle Funktionen und Einstellungen' :
                    form.role === 'admin' ? 'Verwaltung von Benutzern und Einstellungen' :
                    form.role === 'editor' ? 'Bearbeitung von Inhalten und Konfigurationen' :
                    form.role === 'viewer' ? 'Nur Lesezugriff auf alle Bereiche' :
                    'Standard-Mitglied mit eingeschränkten Rechten'
                  }
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Abbrechen</Button>
          <Button 
            onClick={handleSaveMember} 
            disabled={saving || !form.user_id || !form.role} 
            variant="contained"
          >
            {saving ? 'Speichern...' : (editingMember ? 'Aktualisieren' : 'Hinzufügen')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanyMembers;
